/* eslint-disable @typescript-eslint/no-explicit-any */
import mapboxgl from 'mapbox-gl';
import {
  STAGE_COUNT,
  STAGE_THRESHOLDS,
  WeightedPoint,
  areaWeight,
  polygonAreaM2,
  stageCoins,
} from './demandCoins';

export { stageForWeight } from './demandCoins';

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// Demand data (real area interests from GET /area-interests/demand), injected by
// the page before addDemandLayers.
export type Circle = { lng: number; lat: number; radius_m: number };
export type LngLat = { lng: number; lat: number };
export type DemandData = {
  circles: Circle[];
  neighbourhoods: GeoJSON.FeatureCollection;
  // Single-building "could buy" interests — buy demand too, combined into the stage coins.
  buy_points?: LngLat[];
};
let CIRCLES: Circle[] = [];
let NEIGHBOURHOODS: GeoJSON.FeatureCollection = EMPTY;
let BUY_POINTS: LngLat[] = [];
// The stage coins for the current data, computed once per payload: placing
// them compares every interest with its neighbours and every coin at every zoom.
let STAGE_COINS: GeoJSON.FeatureCollection = EMPTY;
// Every stage coin represents buying interest. Keep the visibility separately
// from the Mapbox layer so late data loads and style reloads preserve filters.
let VISIBLE = true;
export function setDemandData(data: DemandData): void {
  CIRCLES = data?.circles ?? [];
  NEIGHBOURHOODS = data?.neighbourhoods ?? EMPTY;
  BUY_POINTS = data?.buy_points ?? [];
  STAGE_COINS = stageCoinFeatures();
}

// The stage coins own every zoom below this: they fade out over
// [COIN_SWITCH_ZOOM - 1, COIN_SWITCH_ZOOM], and only after they are fully gone
// do the per-building buyer coin stacks (BuildingMap's off-market buy markers)
// fade in, over [COIN_SWITCH_ZOOM, COIN_SWITCH_ZOOM + 1] — sequential, never
// overlapping, so the two coin systems don't pile up in the same view.
// Deliberately deep into street zoom: the per-building stacks only appear once
// the user is clearly inspecting particular buildings.
export const COIN_SWITCH_ZOOM = 17;

// The real off-market buy/sell symbol layers (added by BuildingMap when data loads); the demand overlay sits UNDER them.
// The sell-dot layer (decluttered maps) sits below the pill layer, so it's listed first — `find` picks the bottom-most present marker as the anchor.
const MARKER_LAYERS = ['building-off-market-sell-dot', 'building-off-market-sell-icon', 'building-off-market-sell-icon-selected', 'building-off-market-buy-icon'];

const mPerDegLat = 111320;
const mPerDegLng = (lat: number) => 111320 * Math.cos((lat * Math.PI) / 180);

// A removed map keeps its methods but drops its style, so getSource/getLayer/
// hasImage throw instead of returning undefined. Callers outlive the map here:
// hover handlers hold a ref, the demand fetch and icon loads resolve late.
function isLiveMap(map: mapboxgl.Map | null | undefined): map is mapboxgl.Map {
  return !!map && !map._removed;
}

export function setDemandVisible(map: mapboxgl.Map | null, visible: boolean): void {
  VISIBLE = visible;
  if (!isLiveMap(map) || !map.getLayer('interest-stage-coin')) return;
  try {
    for (const id of ['interest-stage-coin', 'interest-anchor-coin']) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    }
  } catch {
    // The style may be reloading between the layer check and update.
  }
  if (!visible) setAreaHighlight(map, null);
}

/**
 * Screen distance in pixels from `point` to a feature's own anchor, or
 * Infinity when the feature carries no point to measure against.
 *
 * The marker layers carry an icon-offset that grows with zoom, so a pill
 * renders up to ten pixels off its anchor at the zooms coins appear at. The
 * offset is not read here: leaving it out makes a marker measure slightly
 * nearer than it looks, which biases ties toward the listing — the direction
 * JAA-329 #16 asked for.
 */
function anchorDistance(
  map: mapboxgl.Map,
  feature: mapboxgl.MapboxGeoJSONFeature,
  point: { x: number; y: number },
): number {
  if (feature.geometry.type !== 'Point') return Infinity;
  const projected = map.project(feature.geometry.coordinates as [number, number]);
  return Math.hypot(projected.x - point.x, projected.y - point.y);
}

/**
 * The area-interest stage coin under a screen point, as its interest weight
 * and anchor coordinates — null when none is hit. Guarded for style
 * reloads (missing layer) and for the fade band: the stage coins fade out
 * linearly over [COIN_SWITCH_ZOOM - 1, COIN_SWITCH_ZOOM] but stay placed, so
 * they answer hits only while still at least half opaque — a clearly visible
 * coin gets its tooltip, a nearly invisible one does not.
 *
 * A listing still wins the point it sits on (JAA-329 #16), but by proximity
 * rather than by veto: a sell pill is a wide rounded rect, so its hit box
 * reaches well past the marker itself and used to swallow every coin it
 * merely overlapped. Whichever anchor is nearer the cursor answers, and a tie
 * goes to the listing.
 */
export function demandCoinAt(
  map: mapboxgl.Map,
  point: { x: number; y: number },
): { weight: number; lngLat: [number, number] } | null {
  if (!isLiveMap(map) || !VISIBLE) return null;
  if (map.getZoom() >= COIN_SWITCH_ZOOM - 0.5) return null;
  if (!map.getLayer('interest-stage-coin')) return null;
  let hits: mapboxgl.MapboxGeoJSONFeature[] = [];
  let markerHits: mapboxgl.MapboxGeoJSONFeature[] = [];
  try {
    const markerLayers = MARKER_LAYERS.filter((id) => map.getLayer(id));
    markerHits = markerLayers.length
      ? map.queryRenderedFeatures(point as mapboxgl.PointLike, { layers: markerLayers })
      : [];
    hits = map.queryRenderedFeatures(point as mapboxgl.PointLike, { layers: ['interest-stage-coin'] });
  } catch {
    return null;
  }
  const f = hits[0];
  if (!f || f.geometry.type !== 'Point') return null;
  const coinDistance = anchorDistance(map, f, point);
  for (const marker of markerHits) {
    if (anchorDistance(map, marker, point) <= coinDistance) return null;
  }
  const props = f.properties as { w?: number } | null;
  const weight = Number(props?.w ?? 1);
  return { weight, lngLat: f.geometry.coordinates as [number, number] };
}

// Stage icon per coin's interest weight — the mapping stageForWeight makes in JS.
const stageIconExpr = (): any => [
  'step', ['coalesce', ['get', 'w'], 1],
  'area-coin-1',
  STAGE_THRESHOLDS[0], 'area-coin-2',
  STAGE_THRESHOLDS[1], 'area-coin-3',
  STAGE_THRESHOLDS[2], 'area-coin-4',
  STAGE_THRESHOLDS[3], 'area-coin-5',
];
// Register the five stage coin images (PNGs exported at 2x from Figma); loaded async, the symbol layer paints once they arrive.
function registerStageIcons(map: mapboxgl.Map): void {
  for (let stage = 1; stage <= STAGE_COUNT; stage++) {
    const id = 'area-coin-' + stage;
    if (map.hasImage(id)) continue;
    map.loadImage(`${process.env.PUBLIC_URL}/assets/markers/area-coin--${stage}--default.png`, (err, img) => {
      if (err || !img || !isLiveMap(map) || map.hasImage(id)) return;
      map.addImage(id, img, { pixelRatio: 2 });
    });
  }
}

// Every buying-interest signal as a weighted point: a single-building buy
// interest weighs 1 at its building, and an area interest (a circle or a
// district) weighs the share of it that falls inside one coin area, at its
// centre.
function interestPoints(): WeightedPoint[] {
  const points: WeightedPoint[] = [];
  CIRCLES.forEach((c) => points.push({ lng: c.lng, lat: c.lat, w: areaWeight(1, Math.PI * c.radius_m ** 2) }));
  BUY_POINTS.forEach((p) => points.push({ lng: p.lng, lat: p.lat, w: 1 }));
  NEIGHBOURHOODS.features.forEach((f) => {
    const count = Number(f.properties?.count ?? 0);
    if (count <= 0) return;
    points.push({
      lng: Number(f.properties?.clng),
      lat: Number(f.properties?.clat),
      w: areaWeight(count, polygonAreaM2(f.geometry)),
    });
  });
  return points;
}

// One feature per stage coin, carrying its interest weight (w) and the lowest
// whole zoom it shows from (minzoom); the last zoom the coins show at is the
// one before COIN_SWITCH_ZOOM.
function stageCoinFeatures(): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: stageCoins(interestPoints(), COIN_SWITCH_ZOOM - 1).map(({ lng, lat, w, minzoom }) => ({
      type: 'Feature',
      properties: { w, minzoom },
      geometry: { type: 'Point', coordinates: [lng, lat] },
    })),
  };
}

// Repoint the already-added overlay at fresh data, for when an interest is
// published while the map is open. A no-op before the layers exist — the
// initial addDemandLayers will pick the new data up from the module state.
export function refreshDemandLayers(map: mapboxgl.Map): void {
  if (!isLiveMap(map)) return;
  (map.getSource('interest-stage-pt') as mapboxgl.GeoJSONSource | undefined)?.setData(STAGE_COINS);
  (map.getSource('interest-anchor-pt') as mapboxgl.GeoJSONSource | undefined)?.setData(circleAnchorPoints());
}

// The circle interests alone, one coin each, for the close-zoom layer. A "± 500 m"
// interest is drawn around one anchor building, so it has a real point to sit on
// once the stage coins hand over — unlike a district, whose coin marks only a
// centroid and would be meaningless on a street.
function circleAnchorPoints(): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: CIRCLES.map((c) => ({
      type: 'Feature',
      properties: { w: 1 },
      geometry: { type: 'Point', coordinates: [c.lng, c.lat] },
    })),
  };
}

// Add the demand overlay once: 5-stage coin icons showing the density of buying
// interest (area + building) at city/area zoom.
// Above COIN_SWITCH_ZOOM the layer cuts off and the per-building buyer coin stacks
// (faded in over the same band by BuildingMap) take over.
export function addDemandLayers(map: mapboxgl.Map): void {
  if (!isLiveMap(map) || map.getSource('interest-stage-pt')) return;
  map.addSource('interest-stage-pt', { type: 'geojson', data: STAGE_COINS });
  registerStageIcons(map);

  map.addLayer({
    id: 'interest-stage-coin', type: 'symbol', source: 'interest-stage-pt', maxzoom: COIN_SWITCH_ZOOM,
    // Mapbox evaluates zoom in a filter per whole zoom level only, so each
    // level shows one fixed set of coins, spaced so that none overlap.
    filter: ['>=', ['zoom'], ['get', 'minzoom']],
    layout: {
      'icon-image': stageIconExpr(),
      'icon-allow-overlap': true,
      // A coin must not reserve collision space that hides a sell marker.
      'icon-ignore-placement': true,
      visibility: VISIBLE ? 'visible' : 'none',
    },
    // Fade out over the last zoom band so the arriving per-building coins overlap it — no empty gap.
    paint: { 'icon-opacity': ['interpolate', ['linear'], ['zoom'], COIN_SWITCH_ZOOM - 1, 1, COIN_SWITCH_ZOOM, 0] as any },
  });

  // The stage coins stop at COIN_SWITCH_ZOOM, and only building-level listings
  // take over from there — so a circle interest used to vanish exactly when the
  // buyer zoomed in to look at it. Its anchor coin carries on from where the
  // stage coins left off (JAA-328).
  map.addSource('interest-anchor-pt', { type: 'geojson', data: circleAnchorPoints() });
  map.addLayer({
    id: 'interest-anchor-coin',
    type: 'symbol',
    source: 'interest-anchor-pt',
    minzoom: COIN_SWITCH_ZOOM,
    layout: {
      'icon-image': stageIconExpr(),
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      visibility: VISIBLE ? 'visible' : 'none',
    },
  });

  // Hover highlight for one area interest's shape (its 500 m circle or district
  // polygon): a green outline + faint fill, matching the clicked-building green.
  // Added last so the highlight paints over the coins.
  map.addSource('area-highlight', { type: 'geojson', data: EMPTY });
  map.addLayer({ id: 'area-highlight-fill', type: 'fill', source: 'area-highlight', paint: { 'fill-color': '#00805A', 'fill-opacity': 0.08 } });
  map.addLayer({ id: 'area-highlight-line', type: 'line', source: 'area-highlight', paint: { 'line-color': '#00805A', 'line-width': 2 } });
}

// Set (or clear with null) the area-interest hover highlight geometry.
export function setAreaHighlight(map: mapboxgl.Map, geometry: GeoJSON.Geometry | null): void {
  if (!isLiveMap(map)) return;
  const src = map.getSource('area-highlight') as mapboxgl.GeoJSONSource | undefined;
  if (!src) return;
  src.setData(geometry ? { type: 'Feature', properties: {}, geometry } : EMPTY);
}

// A ~circle polygon (64-gon) of radius_m around a centre, to highlight a "within
// 500 m" interest. Equirectangular metres->degrees (fine at this small scale).
export function circlePolygon(lng: number, lat: number, radiusM: number): GeoJSON.Polygon {
  const ring: [number, number][] = [];
  const dLat = radiusM / mPerDegLat;
  const dLng = radiusM / mPerDegLng(lat);
  for (let i = 0; i <= 64; i++) {
    const a = (i / 64) * 2 * Math.PI;
    ring.push([lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return { type: 'Polygon', coordinates: [ring] };
}

// District polygon geometry from the already-loaded demand neighbourhoods — the
// demand data carries the polygons, so no extra fetch. Matched by the area key the
// backend groups demand by (legacy place_ids and OSM ids of one area share it), and by
// name only for a feed that predates the key, where two areas named "Keskusta" collide.
export function neighbourhoodGeometry(key: string, name: string): GeoJSON.Geometry | null {
  const f =
    NEIGHBOURHOODS.features.find((x) => key && x.properties?.ext_id === key) ??
    NEIGHBOURHOODS.features.find((x) => !x.properties?.ext_id && x.properties?.name === name);
  return (f?.geometry as GeoJSON.Geometry) ?? null;
}

// One MultiPolygon out of several Polygon/MultiPolygon geometries, so an interest in
// several areas highlights all of them at once; null when there is nothing to draw.
export function mergePolygons(geometries: GeoJSON.Geometry[]): GeoJSON.Geometry | null {
  const polygons = geometries.flatMap((g) => {
    if (g.type === 'Polygon') return [g.coordinates];
    if (g.type === 'MultiPolygon') return g.coordinates;
    return [];
  });
  if (polygons.length === 0) return null;
  return { type: 'MultiPolygon', coordinates: polygons };
}

// Render order, bottom to top: the stage coins, the real buy/sell
// markers, then the anchor coin. The aggregate overlay stays under the markers
// so it never hides a real listing, but the anchor coin marks ONE interest the
// buyer owns, at the anchor building's own coordinates — so a listing marker on
// that same building would sit exactly on top of it and the circle interest
// would vanish at street zoom, which is the bug the coin exists to fix
// (JAA-328). It is kept under the hover highlight, which is meant to paint over
// the coins. The stage coins ARE hit-tested (demandCoinAt, for the JAA-329
// tooltip), but demandCoinAt yields to any marker layer at the same point, so
// layer order and click priority stay consistent: markers above, markers win.
export function orderDemandLayers(map: mapboxgl.Map): void {
  if (!isLiveMap(map)) return;
  const marker = MARKER_LAYERS.find((id) => map.getLayer(id));
  if (marker && map.getLayer('interest-stage-coin')) {
    try { map.moveLayer('interest-stage-coin', marker); } catch { /* layer race */ }
  }
  if (map.getLayer('interest-anchor-coin')) {
    const highlight = map.getLayer('area-highlight-fill') ? 'area-highlight-fill' : undefined;
    try { map.moveLayer('interest-anchor-coin', highlight); } catch { /* layer race */ }
  }
}
